var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
import { useState, useRef, useCallback, useEffect } from 'react';
import { base64ToArrayBuffer, convertPCM16ToFloat32 } from '../utils/audio';
/**
 * Hook for managing audio playback for real-time voice mode
 *
 * @param analytics - pcln logger instance
 * @returns object containing state and queue management functions for playback
 * - `isPlaying` - whether or not audio is currently playing
 * - `queueLength` - the number of items currently waiting playback
 * - `playAudio` - function that queues an item up for playback
 * - `stopPlayback` - stops all currently playing audio and clears the queue
 * - `clearQueue` - clears the audio playback queue
 */
export function useRealTimeAudioPlayer(analytics) {
  const [isPlaying, setIsPlaying] = useState(false);
  // Refs for managing audio playback state
  const audioContextRef = useRef(null);
  const audioQueueRef = useRef([]);
  const currentSourceRef = useRef(null);
  const playNextInQueue = useCallback(() => __awaiter(this, void 0, void 0, function* () {
    // Check if playback should be interrupted
    const isAudioInterrupted = localStorage.getItem('isAudioInterrupted') === 'yes';
    if (isAudioInterrupted) {
      audioQueueRef.current = [];
      setIsPlaying(false);
      return;
    }
    // If queue is empty, stop playing
    if (audioQueueRef.current.length === 0) {
      setIsPlaying(false);
      return;
    }
    const {
      base64AudioData,
      sampleRate
    } = audioQueueRef.current.shift();
    try {
      // Initialize or resume AudioContext
      if (!audioContextRef.current) {
        audioContextRef.current = new window.AudioContext();
      }
      if (audioContextRef.current.state === 'suspended') {
        yield audioContextRef.current.resume();
      }
      // Convert base64 audio data to playable format
      const arrayBuffer = base64ToArrayBuffer(base64AudioData);
      const pcm16Array = new Int16Array(arrayBuffer);
      const float32Array = convertPCM16ToFloat32(pcm16Array);
      // Validate audio data
      if (!float32Array || float32Array.some(isNaN)) {
        console.warn('Invalid audio data received, skipping to next chunk');
        yield playNextInQueue();
        return;
      }
      // Create and setup audio buffer
      const buffer = audioContextRef.current.createBuffer(1, float32Array.length, sampleRate);
      buffer.copyToChannel(float32Array, 0);
      // Create and setup source node
      const source = audioContextRef.current.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContextRef.current.destination);
      // Store current source for potential interruption
      currentSourceRef.current = source;
      // Setup completion handler
      source.onended = () => {
        currentSourceRef.current = null;
        if (audioQueueRef.current.length > 0) {
          playNextInQueue().catch(e => {
            console.warn('Unable to play next audio file');
          });
        } else {
          setIsPlaying(false);
          audioContextRef.current = null;
        }
      };
      // Start playback
      source.start();
      setIsPlaying(true);
    } catch (error) {
      analytics.logError({
        loggedFrom: 'useRealTimeAudioPlayer.playNextInQueue',
        message: 'Failed to play audio chunk',
        error
      });
      // Attempt to continue with next chunk
      yield playNextInQueue();
    }
  }), []);
  const playAudio = useCallback((base64AudioData, sampleRate = 24000) => __awaiter(this, void 0, void 0, function* () {
    // Add new audio chunk to queue
    audioQueueRef.current.push({
      base64AudioData,
      sampleRate
    });
    // Start playback if not already playing
    if (!isPlaying) {
      yield playNextInQueue();
    }
  }), [isPlaying, playNextInQueue]);
  const stopPlayback = useCallback(() => __awaiter(this, void 0, void 0, function* () {
    // Stop current playback
    if (currentSourceRef.current) {
      currentSourceRef.current.stop();
      currentSourceRef.current = null;
    }
    // Close and cleanup audio context
    const audioContext = audioContextRef.current;
    if (audioContext && audioContext.state !== 'closed') {
      yield audioContext.close();
      // At this point, we should check if audioContextRef.current is still
      // the same instance we started with
      if (audioContextRef.current === audioContext) {
        audioContextRef.current = null;
      }
    }
    setIsPlaying(false);
  }), []);
  const clearQueue = useCallback(() => __awaiter(this, void 0, void 0, function* () {
    audioQueueRef.current = [];
    yield stopPlayback();
  }), [stopPlayback]);
  useEffect(() => {
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      clearQueue();
    };
  }, []);
  return {
    isPlaying,
    queueLength: audioQueueRef.current.length,
    playAudio,
    stopPlayback,
    clearQueue
  };
}